import { StockRow, StockVarianceState } from "../../../../api/SiteDaily";
import { UNCATEGORIZED } from "./materialCategories";

// The draft state and arithmetic behind the Store-Stock day sheet. No JSX, so
// the ledger maths can be read on its own.
//
// This mirrors the server's stock-ledger.util exactly — the screen must show the
// closing balance that WILL be stored, not an approximation of it:
//
//   avgRate      = (openingValue + purchasedValue) / (openingQty + purchasedQty)
//   issuedValue  = issuedQty     x avgRate
//   adjustValue  = adjustmentQty x avgRate     (signed, at cost)
//   closingQty   = openingQty   + purchasedQty   + adjustmentQty   - issuedQty
//   closingValue = openingValue + purchasedValue + adjustmentValue - issuedValue

/** Below this, a quantity is decimal noise rather than stock. */
export const QTY_EPSILON = 0.0001;

export const r4 = (value: number): number =>
  Number.isFinite(value) ? Math.round((value + Number.EPSILON) * 10000) / 10000 : 0;

export const num = (value: string): number => {
  const parsed = Number(value);
  return value === "" || !Number.isFinite(parsed) ? 0 : parsed;
};

/** The columns the store fills in. Everything else on a row is derived. */
export interface StockDraft {
  openingQty: string;
  openingRate: string;
  purchasedQty: string;
  purchasedRate: string;
  issuedQty: string;
  // The rate the store states it issued at. It does NOT value the issue — the
  // day's weighted average cost does — so it never enters figuresOf.
  issuedRate: string;
  adjustmentQty: string;
  adjustmentReason: string;
  remarks: string;
}

export const draftOf = (row: StockRow): StockDraft => ({
  // The opening comes down filled on every row. On a carried row it is
  // read-only (the server rejects an opening that contradicts the carry), and
  // only the first day of a material lets it be typed.
  openingQty: row.openingQty,
  openingRate: row.openingRate,
  purchasedQty: row.purchasedQty,
  purchasedRate: row.purchasedRate,
  issuedQty: row.issuedQty,
  issuedRate: row.issuedRate,
  adjustmentQty: row.adjustmentQty,
  adjustmentReason: row.adjustmentReason,
  remarks: row.remarks,
});

export const sameDraft = (a: StockDraft | undefined, b: StockDraft | undefined) =>
  !!a &&
  !!b &&
  a.openingQty === b.openingQty &&
  a.openingRate === b.openingRate &&
  a.purchasedQty === b.purchasedQty &&
  a.purchasedRate === b.purchasedRate &&
  a.issuedQty === b.issuedQty &&
  a.issuedRate === b.issuedRate &&
  a.adjustmentQty === b.adjustmentQty &&
  a.adjustmentReason === b.adjustmentReason &&
  a.remarks === b.remarks;

/** Did the store type anything on this row at all? */
export const hasMovement = (draft: StockDraft | undefined): boolean =>
  !!draft &&
  (num(draft.purchasedQty) > 0 ||
    num(draft.issuedQty) > 0 ||
    Math.abs(num(draft.adjustmentQty)) >= QTY_EPSILON ||
    num(draft.issuedRate) > 0 ||
    draft.adjustmentReason !== "" ||
    draft.remarks !== "");

export interface StockFigures {
  openingQty: number;
  openingValue: number;
  purchasedQty: number;
  purchasedValue: number;
  issuedQty: number;
  issuedValue: number;
  adjustmentQty: number;
  adjustmentValue: number;
  avgRate: number | null;
  closingQty: number;
  closingValue: number;
}

/**
 * The row's live figures from its draft — the same arithmetic the server will
 * apply on save, so what the clerk reads is what gets stored.
 *
 * The opening is taken from the ROW (the carried balance) unless nothing carried
 * in, in which case the draft's typed opening is the anchor.
 */
export const figuresOf = (row: StockRow, draft: StockDraft | undefined): StockFigures => {
  const carried = row.carriedFrom !== "";
  const openingQty = carried ? num(row.openingQty) : num(draft?.openingQty ?? row.openingQty);
  const openingValue = carried
    ? num(row.openingValue)
    : r4(num(draft?.openingQty ?? "") * num(draft?.openingRate ?? ""));

  const purchasedQty = num(draft?.purchasedQty ?? "");
  const purchasedRate = num(draft?.purchasedRate ?? "");
  const purchasedValue = purchasedQty > 0 && purchasedRate > 0 ? r4(purchasedQty * purchasedRate) : 0;
  const issuedQty = num(draft?.issuedQty ?? "");
  const adjustmentQty = num(draft?.adjustmentQty ?? "");

  const baseQty = r4(openingQty + purchasedQty);
  const baseValue = r4(openingValue + purchasedValue);
  // Nothing in stock to average against -> the last known average, then what the
  // tasks paid. Never 0: that would value every issue at nothing.
  const fallback = num(row.avgRate) || num(row.taskIssuedRate);
  const avgRate =
    baseQty > QTY_EPSILON && baseValue > 0
      ? r4(baseValue / baseQty)
      : fallback > 0
        ? r4(fallback)
        : null;

  const issuedValue = avgRate === null ? 0 : r4(issuedQty * avgRate);
  const adjustmentValue = avgRate === null ? 0 : r4(adjustmentQty * avgRate);
  const closingQty = r4(openingQty + purchasedQty + adjustmentQty - issuedQty);
  let closingValue = r4(openingValue + purchasedValue + adjustmentValue - issuedValue);
  // An empty bin is worth nothing — otherwise rounding leaves paise stranded
  // against no quantity and the next day averages money that is not there.
  if (Math.abs(closingQty) < QTY_EPSILON) closingValue = 0;

  return {
    openingQty,
    openingValue,
    purchasedQty,
    purchasedValue,
    issuedQty,
    issuedValue,
    adjustmentQty,
    adjustmentValue,
    avgRate,
    closingQty,
    closingValue,
  };
};

/**
 * The store's issued quantity against what the tasks booked.
 *
 * A unit mismatch is decided BEFORE any arithmetic: a quantity booked in bags
 * cannot be subtracted from one held in MT, and doing it anyway would invent a
 * variance that does not exist. The server sends that verdict; the only thing
 * recomputed here is the live comparison while the clerk types.
 */
export const varianceOf = (
  row: StockRow,
  draft: StockDraft | undefined
): { state: StockVarianceState; diff: number | null } => {
  if (row.varianceState === "UNIT_MISMATCH") return { state: "UNIT_MISMATCH", diff: null };
  if (row.taskIssuedQty === "") return { state: "NO_TASK_ISSUE", diff: null };
  const diff = r4(num(draft?.issuedQty ?? "") - num(row.taskIssuedQty));
  if (Math.abs(diff) < QTY_EPSILON) return { state: "MATCH", diff: 0 };
  return { state: diff > 0 ? "STORE_HIGHER" : "STORE_LOWER", diff };
};

/**
 * Does the row answer this search?
 *
 * Matched against the material name, its product code and its category, so
 * "rocktools" narrows to a category and "starter pipe" to a material. Every
 * whitespace-separated term must appear, which is what makes a two-word search
 * useful on names as long as "89mm Starter Pipe 3Mtr-5.49mm Standard".
 *
 * Presentation only. The save's replace-set is built from the SHEET's rows, never
 * the filtered view — sending only what is visible would delete every material
 * the search happened to hide.
 */
export const matchesSearch = (row: StockRow, query: string): boolean => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = `${row.materialName} ${row.productCode} ${row.categoryName}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

export interface StockCategoryGroup {
  key: string; // `CAT-${categoryId}` — the same convention the material surfaces use
  categoryId: number;
  categoryName: string;
  // The SAME row objects, never copies: drafts are keyed on materialId and must
  // keep pointing at the sheet's own rows.
  rows: StockRow[];
  materialCount: number;
  movementCount: number;
  openingValue: number;
  purchasedValue: number;
  issuedValue: number;
  adjustmentValue: number;
  closingValue: number;
}

/**
 * Rows grouped by material category, by category NAME ascending with
 * uncategorised last.
 *
 * Deliberately not by value (which is how the material panel orders its
 * blocks): a store register is read the same way every day, and a value order
 * would shuffle the whole sheet as a quantity is typed.
 */
export const groupByCategory = (
  rows: StockRow[],
  figuresFor: (row: StockRow) => StockFigures,
  movedFor: (row: StockRow) => boolean
): StockCategoryGroup[] => {
  const byCategory = new Map<number, StockCategoryGroup>();
  rows.forEach((row) => {
    const categoryId = Number(row.categoryId) || 0;
    let group = byCategory.get(categoryId);
    if (!group) {
      group = {
        key: `CAT-${categoryId}`,
        categoryId,
        categoryName: row.categoryName || UNCATEGORIZED,
        rows: [],
        materialCount: 0,
        movementCount: 0,
        openingValue: 0,
        purchasedValue: 0,
        issuedValue: 0,
        adjustmentValue: 0,
        closingValue: 0,
      };
      byCategory.set(categoryId, group);
    }
    const figures = figuresFor(row);
    group.rows.push(row);
    group.materialCount += 1;
    if (movedFor(row)) group.movementCount += 1;
    group.openingValue += figures.openingValue;
    group.purchasedValue += figures.purchasedValue;
    group.issuedValue += figures.issuedValue;
    group.adjustmentValue += figures.adjustmentValue;
    group.closingValue += figures.closingValue;
  });

  return Array.from(byCategory.values()).sort((a, b) => {
    if (a.categoryId === 0) return 1;
    if (b.categoryId === 0) return -1;
    return a.categoryName.localeCompare(b.categoryName);
  });
};

/** The day's totals across every category, for the section header. */
export const dayTotals = (groups: StockCategoryGroup[]) =>
  groups.reduce(
    (acc, group) => ({
      openingValue: acc.openingValue + group.openingValue,
      purchasedValue: acc.purchasedValue + group.purchasedValue,
      issuedValue: acc.issuedValue + group.issuedValue,
      adjustmentValue: acc.adjustmentValue + group.adjustmentValue,
      closingValue: acc.closingValue + group.closingValue,
      materialCount: acc.materialCount + group.materialCount,
      movementCount: acc.movementCount + group.movementCount,
    }),
    {
      openingValue: 0,
      purchasedValue: 0,
      issuedValue: 0,
      adjustmentValue: 0,
      closingValue: 0,
      materialCount: 0,
      movementCount: 0,
    }
  );

/** The payload row for a save — only what the store owns. */
export const saveRowOf = (row: StockRow, draft: StockDraft) => ({
  materialId: row.materialId,
  // An anchored opening is only meaningful when nothing carried in; sending it
  // on a carried row would be rejected, so it is left out there.
  ...(row.carriedFrom === ""
    ? { openingQty: draft.openingQty, openingRate: draft.openingRate }
    : {}),
  purchasedQty: draft.purchasedQty,
  purchasedRate: draft.purchasedRate,
  issuedQty: draft.issuedQty,
  issuedRate: draft.issuedRate,
  adjustmentQty: draft.adjustmentQty,
  adjustmentReason: draft.adjustmentReason,
  remarks: draft.remarks,
});
